import { useCallback, useState } from 'react'
import { MAX_ROWS, SAMPLES } from '../../data/atomizerSamples'
import type { AtomRow } from '../../data/atomizerSamples'
import { extractRules } from '../../utils/extractRules'
import { FORM_ENDPOINT } from '../../data/links'

export type AtomResult = {
  id: string
  label: string
  rows: AtomRow[]
  /** Totals for the whole document; the ledger shows only `rows`. */
  total: number
  binding?: number
  conflicts?: number
}
type SendState = 'idle' | 'sending' | 'sent' | 'error'

const MAX_BYTES = 5 * 1024 * 1024

export function useAtomizer() {
  const [result, setResult] = useState<AtomResult | null>(null)
  const [message, setMessage] = useState('')
  const [libOpen, setLibOpen] = useState(false)
  const [send, setSend] = useState<SendState>('idle')

  const show = useCallback((next: AtomResult) => {
    setResult(next)
    setMessage('')
    setLibOpen(false)
    setSend('idle')
  }, [])

  const runSample = useCallback(
    (id: string) => {
      const s = SAMPLES.find((x) => x.id === id)
      if (s)
        show({
          id: s.id,
          label: s.file,
          rows: s.rows.slice(0, MAX_ROWS),
          total: s.total,
          binding: s.binding,
          conflicts: s.conflicts,
        })
    },
    [show],
  )

  const runText = useCallback(
    (text: string, label: string) => {
      const rows = extractRules(text, MAX_ROWS)
      if (!rows.length) {
        setResult(null)
        setMessage('No obligation language found. Try text with must, never, shall, or should in it.')
        return
      }
      show({ id: `text-${Date.now()}`, label, rows, total: rows.length })
    },
    [show],
  )

  const runFile = useCallback(
    (file: File) => {
      if (file.size > MAX_BYTES) {
        setMessage('That file is over 5 MB.')
        return
      }
      const name = file.name.toLowerCase()
      if (name.endsWith('.txt') || name.endsWith('.md')) {
        file.text().then((t) => runText(t, `${file.name} · ${name.endsWith('.md') ? 'MD' : 'TXT'}`))
        return
      }
      // PDF and Word need server-side parsing; the preview reads plain text only.
      setResult(null)
      setMessage('PDF and Word files are read in the full platform. Paste the text below, or try one of ours.')
    },
    [runText],
  )

  const submitLibrary = useCallback(
    async (fields: { name: string; email: string; company: string }) => {
      setSend('sending')
      try {
        const res = await fetch(FORM_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            _subject: 'Atomizer: start my library',
            ...fields,
            source: result?.label ?? '',
            rules: result?.total ?? 0,
          }),
        })
        setSend(res.ok ? 'sent' : 'error')
      } catch {
        setSend('error')
      }
    },
    [result],
  )

  return { result, message, libOpen, setLibOpen, send, runSample, runText, runFile, submitLibrary }
}
